import { OrderStatus, Track } from "@/types";
import cn from "@/utils/class-names";
import { useRef } from "react";
import { PiArrowDownBold, PiTriangle } from "react-icons/pi";
import { Link } from "react-scroll";
import { Badge, Text, Title } from "rizzui";

const statusColors: {
    [key: string]: string;
} = {
    success: "text-green",
    pending: "text-orange",
    canceled: "text-red",
};

export default function Timeline({
    data,
    className,
    showmoreButton = false,
    order = "asc",
}: {
    data: Track[];
    className?: string;
    showmoreButton?: boolean;
    order?: "asc" | "desc";
}) {
    const buttonEl = useRef<HTMLDivElement>(null!);
    function handleScrollPosition(e: any) {
        buttonEl.current.scrollLeft = e.target.offsetLeft - 30;
    }

    return (
        <div className={cn("relative @container", className)}>
            {data
                .sort(
                    (a, b) =>
                        new Date(b.createdDate ?? "").getTime() -
                        new Date(a.createdDate ?? "").getTime(),
                )
                .map((timeline: Track, index: number) => (
                    <div
                        className="flex items-center"
                        key={`timeline-${index}`}
                    >
                        <div className="hidden w-[147px] flex-shrink-0 @lg:block">
                            <Text
                                as="span"
                                className="pe-5 text-gray-500 @2xl:pe-10"
                            >
                                {new Date(
                                    timeline?.createdDate ?? "",
                                ).toLocaleDateString()}
                            </Text>
                        </div>
                        <div
                            className={cn(
                                "relative flex-grow border-s border-muted py-5 ps-10 before:absolute before:-left-[3px] before:-top-[3px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-gray-200 dark:border-gray-700 dark:before:bg-gray-900 rtl:before:-right-[3px]",
                                index !== 0 && "before:hidden",
                                index === data.length - 1 &&
                                    "before:-bottom-[3px] before:top-auto before:block",
                            )}
                        >
                            <span className="absolute -left-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-gray-0 dark:bg-gray-50 rtl:-right-3">
                                {false ? (
                                    <PiTriangle className="h-5 w-5 rotate-180" />
                                ) : order === "asc" ? (
                                    <PiTriangle className="h-5 w-5 rotate-180" />
                                ) : (
                                    <PiTriangle className="h-5 w-5" />
                                )}
                            </span>
                            <Title
                                as="h3"
                                className={cn(
                                    "mb-3 flex items-center text-base font-semibold",
                                    timeline.status
                                        ? statusColors[timeline.status]
                                        : "text-gray-900",
                                )}
                            >
                                {timeline.location}
                            </Title>
                            <div className="relative -ms-10">
                                <div className="ps-10">
                                    <Text className="text-sm font-normal leading-loose text-gray-500">
                                        {timeline.status ===
                                        OrderStatus.Delivered ? (
                                            <Badge
                                                color="success"
                                                variant="flat"
                                                rounded="md"
                                            >
                                                {timeline.status}
                                            </Badge>
                                        ) : timeline.status ===
                                          OrderStatus.Requested ? (
                                            <Badge
                                                color="warning"
                                                variant="flat"
                                                rounded="md"
                                            >
                                                {timeline.status}
                                            </Badge>
                                        ) : timeline.status ===
                                          OrderStatus.Cancelled ? (
                                            <Badge
                                                color="danger"
                                                variant="flat"
                                                rounded="md"
                                            >
                                                {timeline.status}
                                            </Badge>
                                        ) : timeline.status ===
                                          OrderStatus.Processing ? (
                                            <Badge
                                                color="secondary"
                                                variant="flat"
                                                rounded="md"
                                            >
                                                {timeline.status}
                                            </Badge>
                                        ) : timeline.status ===
                                          OrderStatus.Purchased ? (
                                            <Badge
                                                color="primary"
                                                variant="flat"
                                                rounded="md"
                                            >
                                                {timeline.status}
                                            </Badge>
                                        ) : timeline.status ===
                                          OrderStatus.InTransit ? (
                                            <Badge
                                                color="info"
                                                variant="flat"
                                                rounded="md"
                                            >
                                                {timeline.status}
                                            </Badge>
                                        ) : (
                                            <></>
                                        )}
                                        <Text
                                            as="span"
                                            className="block font-medium text-gray-700"
                                        >
                                            {timeline.status ==
                                            OrderStatus.Delivered
                                                ? "Delivered, This order has been delivered."
                                                : timeline.status ==
                                                    OrderStatus.InTransit
                                                  ? "In transit to delivery location."
                                                  : timeline.status ==
                                                      OrderStatus.Purchased
                                                    ? "Product has been purchased, waiting for shipment."
                                                    : timeline.status ==
                                                        OrderStatus.Processing
                                                      ? "Order is being processed, waiting for purchase."
                                                      : timeline.status ==
                                                          OrderStatus.Requested
                                                        ? "Order has been received, waiting for confirmation."
                                                        : timeline.status ==
                                                            OrderStatus.Cancelled
                                                          ? "Order has been cancelled."
                                                          : null}
                                        </Text>{" "}
                                        {new Date(
                                            timeline?.createdDate ?? "",
                                        ).toLocaleTimeString()}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

            {showmoreButton && (
                <div className="flex items-center" ref={buttonEl}>
                    <div className="hidden w-[147px] flex-shrink-0 @lg:block"></div>
                    <Link
                        to={"tracking_history"}
                        spy={true}
                        hashSpy={true}
                        smooth={true}
                        duration={500}
                        // @ts-ignore
                        onClick={(e) => handleScrollPosition(e)}
                        className="ms-10 mt-10 flex flex-grow cursor-pointer items-center gap-3 text-sm font-medium text-gray-900"
                    >
                        View Tracking History
                        <PiArrowDownBold className="h-4 w-4 text-gray-500" />
                    </Link>
                </div>
            )}
        </div>
    );
}
